"use client";

// Selected app-folder/tag state — single shared source across the Sidebar
// surfaces that touch it (FolderStrip inside the apps view, SidebarFoldersDrawer
// on the opposite edge, and the main Sidebar nav that consumes the filter).
//
// Persistence: localStorage key "cc-sidebar-app-folder-filter".
// Cross-component sync: a module-level subscriber set lets same-tab writers
// notify all hook instances. The native `storage` event covers cross-tab.

import { useCallback, useEffect, useState } from "react";

const STORAGE_KEY = "cc-sidebar-app-folder-filter";

const subscribers = new Set<() => void>();
function notify(): void {
  for (const fn of subscribers) fn();
}

function read(): string | null {
  if (typeof window === "undefined") return null;
  try {
    return window.localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

export function useSelectedAppFolder() {
  const [selectedId, setSelectedIdState] = useState<string | null>(null);

  useEffect(() => {
    setSelectedIdState(read());

    const onChange = () => setSelectedIdState(read());
    subscribers.add(onChange);

    const onStorage = (e: StorageEvent) => {
      if (e.key === STORAGE_KEY) onChange();
    };
    window.addEventListener("storage", onStorage);

    return () => {
      subscribers.delete(onChange);
      window.removeEventListener("storage", onStorage);
    };
  }, []);

  const setSelectedId = useCallback((id: string | null) => {
    try {
      if (id) window.localStorage.setItem(STORAGE_KEY, id);
      else window.localStorage.removeItem(STORAGE_KEY);
    } catch {
      // Quota / privacy mode — in-memory state still updates below.
    }
    setSelectedIdState(id);
    notify();
  }, []);

  return { selectedId, setSelectedId };
}
